import { useMutation, useQuery, useQueryClient } from "react-query";

import { axiosShyftr, http } from "./http";

const isAuthenticated = () =>
  !!axiosShyftr.defaults.headers.common["Authorization"];

export const cacheKeys = {
  authenticated: "authenticated",
  cases: "cases",
  me: "me",
  otp: "otp",
  shifts: "shifts",
  tokan: "token",
};

export const useAuthenticated = () => {
  return useQuery([cacheKeys.authenticated], () => isAuthenticated());
};

export const useUpdateAuthentication = () => {
  const queryClient = useQueryClient();

  return useMutation((token) => {
    if (token) {
      axiosShyftr.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      queryClient.invalidateQueries([cacheKeys.authenticated]);

      return;
    }

    delete axiosShyftr.defaults.headers.common["Authorization"];
    Object.values(cacheKeys).forEach((cacheKey) => {
      queryClient.invalidateQueries([cacheKey]);
    });
  });
};

export const useCases = () => {
  return useQuery([cacheKeys.cases], http.getCases, {
    cacheTime: Infinity,
    enabled: isAuthenticated(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useMe = () => {
  return useQuery([cacheKeys.me], http.getMe, {
    cacheTime: Infinity,
    enabled: isAuthenticated(),
    staleTime: Infinity,
    refetchIntervalInBackground: false,
  });
};

export const useShifts = () => {
  return useQuery([cacheKeys.shifts], http.getShifts, {
    cacheTime: Infinity,
    enabled: isAuthenticated(),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
};

export const useAuth = () => {
  return useMutation(http.getAuth);
};

export const useToken = () => {
  return useMutation(http.getToken, { mutationKey: cacheKeys.token });
};

export const useRequestShift = () => {
  const queryClient = useQueryClient();
  const { scheduledHours } = queryClient.getQueryData([cacheKeys.me]);

  return useMutation(
    async ({ shifts }) => {
      await http.requestShift(shifts.map(({ shiftId }) => shiftId));
      await new Promise((resolve) => {
        const interval = setInterval(async () => {
          const me = await http.getMe();

          if (
            me.scheduledHours.thisWeek === scheduledHours.thisWeek &&
            me.scheduledHours.nextWeek === scheduledHours.nextWeek &&
            me.scheduledHours.weekAfterNext === scheduledHours.weekAfterNext
          ) {
            return;
          }

          clearInterval(interval);
          queryClient.setQueryData([cacheKeys.me], me);
          resolve();
        }, 1000);
      });
      await queryClient.fetchQuery({ queryKey: [cacheKeys.shifts] });
    },
    {
      onError: async () =>
        await new Promise((resolve) =>
          setTimeout(async () => {
            queryClient.invalidateQueries([cacheKeys.shifts]);
            await queryClient.fetchQuery({ queryKey: [cacheKeys.shifts] });
            resolve();
          }, 3000)
        ),
    }
  );
};

export const useRequestAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ availabilityDate, shiftType }) =>
      await http.requestAvailability({ availabilityDate, shiftType }),
    {
      onSettled: () => queryClient.invalidateQueries([cacheKeys.cases]),
    }
  );
};

export const useEnableAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation(async (caseId) => await http.enableAvailability(caseId), {
    onSettled: () => queryClient.invalidateQueries([cacheKeys.cases]),
  });
};

export const useCancelAvailability = () => {
  const queryClient = useQueryClient();

  return useMutation(async (caseId) => await http.cancelAvailability(caseId), {
    onSettled: () => queryClient.invalidateQueries([cacheKeys.cases]),
  });
};
